import { Component, OnInit, OnDestroy, AfterViewInit, AfterContentInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { RegistrationService } from '../shared/registration.service';
import { SubSink } from 'subsink';
import { Store } from '@ngxs/store';
import { RegistrationState, NotificationStateModel } from '../state/registration.state';
import { catchError, map, tap, finalize } from 'rxjs/operators';
import { Notification } from 'src/app/shared/models/notification.model';
import { DRIVER_ABSTRACT, UPLOAD_STATUS } from 'src/app/shared/services/driver-api.service';
import { ProfileService } from 'src/app/profile/profile.service';
import { DomSanitizer } from '@angular/platform-browser';
import { DocumentRequest } from 'src/app/shared/models/document-request.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { LoadingGifComponent } from 'src/app/shared/loading-gif/loading-gif/loading-gif.component';

const REQUIRED_FIELD_MESSAGE = 'You must enter a value';
const VALID_CHARACTER_MESSAGE = 'Please enter valid input. Special characters allowed are , -';
const ERROR_MESSAGE = 'There was an error, please contact us for help';
const SUCCESS_MESSAGE = 'Your abstract was successfully uploaded';
const INCORRECT_TYPE_MESSAGE = 'The file must be a pdf, jpeg, or jpg';
const SOURCE_TEXT = 'Licensing Authority';
const ISO_8601 = 'YYYY-MM-DDTHH:mm:ss[Z]';

@Component({
    selector: 'app-driver-abstract',
    templateUrl: './driver-abstract.component.html',
    styleUrls: ['./driver-abstract.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      ReactiveFormsModule,
      MatButtonModule,
      MatDatepickerModule,
      MatFormFieldModule,
      MatIconModule,
      MatInputModule,
      MatNativeDateModule,
      LoadingGifComponent
    ]
})
export class DriverAbstractComponent implements OnInit {
  html: any;
  supportEmail = 'support@FleetOperate.com';
  driverSupportEmail = 'driversupport@fleetoperate.com';
  private subs = new SubSink();

  loading: boolean;
  file: any;
  errorMessage: string;
  successMessage: string;
  form: UntypedFormGroup;
  presentDate: Date = new Date();
  uploaded: boolean;
  createdDate: string;
  attempted: boolean;

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly store: Store,
    private readonly profileService: ProfileService,
    private domSanitizer: DomSanitizer,
    private datePipe: DatePipe,
    private readonly fb: UntypedFormBuilder,
    private readonly dateTimeService: DateTimeService
  ) {
    this.loading = true;
    this.errorMessage = undefined;
    this.form = this.createForm();
    this.attempted = false;
  }

  ngOnInit() {
    this.subs.add(
      this.registrationService
        .loadDriver()
        .pipe(map(() => this.store.selectSnapshot(RegistrationState.notification)))
        .subscribe((notificationStateModel: NotificationStateModel) => {
          if (notificationStateModel && notificationStateModel.notifications) {
            this.driverAbstractStatus(
              this.profileService.findNotification(
                [...notificationStateModel.notifications],
                DRIVER_ABSTRACT,
                UPLOAD_STATUS
              )
            );
          }
        })
    );
  }

  onNavigateToProfile(): void {
    this.registrationService.navigateToProfile();
  }

  private driverAbstractStatus(driverAbstract: Notification): void {
    if (driverAbstract && driverAbstract.createdDate) {
      this.createdDate = this.datePipe.transform(driverAbstract.createdDate, 'dd/MM/yyyy') as string;
      this.uploaded = true;
    } else {
      this.uploaded = false;
    }
    this.loading = false;
  }

  onSubmit(): void {
    this.loading = true;
    const model = this.prepareSaveModel();

    if (model === undefined) {
      return;
    }

    this.registrationService
      .saveDocument(model, this.file)
      .pipe(
        tap(() => {
          this.setSuccessMessage(SUCCESS_MESSAGE);
          this.setErrorMessage(undefined);
          const extension = this.file?.name?.split('.').pop()?.toLowerCase() || '';
          this.registrationService.notifyCarriersOnDocumentUpload('Abstract', extension, this.file);
        }),
        catchError((error: string) => {
          this.setErrorMessage(ERROR_MESSAGE);
          this.setSuccessMessage(undefined);
          throw error;
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(() => {
        this.attempted = true;
      });
  }

  setSuccessMessage(message: string) {
    this.successMessage = message;
  }

  setErrorMessage(message: string) {
    this.errorMessage = message;
  }

  onFileChange($event: any): void {
    if (!$event || $event.files.length === 0) {
      return;
    }

    const reader = new FileReader();

    const [file] = $event.files;
    reader.readAsDataURL(file);

    reader.onload = () => {
      this.file = file;
      this.onSubmit();
    };
  }

  private createForm(): UntypedFormGroup {
    const form = this.fb.group({
      reportSource: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9]+([-,\\s][a-zA-Z0-9]+)*\\s*$')]],
      generationDate: ['', Validators.required],
      validToDate: ['', Validators.required]
    });
    return form;
  }

  get reportSource() {
    return this.form.get('reportSource');
  }
  get generationDate() {
    return this.form.get('generationDate');
  }
  get validToDate() {
    return this.form.get('validToDate');
  }

  getReportSourceErrorMessage() {
    return this.reportSource.errors.required
      ? REQUIRED_FIELD_MESSAGE
      : this.reportSource.errors.pattern
      ? VALID_CHARACTER_MESSAGE
      : '';
  }

  getGenerationDateErrorMessage() {
    return this.generationDate.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  getValidToDateErrorMessage() {
    return this.validToDate.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  private prepareSaveModel(): DocumentRequest {
    const extension = this.file.name
      .split('.')
      .pop()
      .toLowerCase();
    const formModel = this.form.value;

    if (['pdf', 'jpeg', 'jpg'].find(element => element == extension) == undefined) {
      this.setErrorMessage(INCORRECT_TYPE_MESSAGE);
      this.setSuccessMessage(undefined);
      return undefined;
    }

    const model = {
      source: SOURCE_TEXT,
      extension: extension,
      generationDate: this.dateTimeService.formatDateTime(formModel.generationDate, ISO_8601),
      validToDate: this.dateTimeService.formatDateTime(formModel.validToDate, ISO_8601),
      reportType: 'Abstract'
    };

    return model as DocumentRequest;
  }
}
